// Phase C — the first-sign-in price popup (founder-approved mockup v2).
//
// A brand-new DJ's account is registered with a per-currency PLACEHOLDER
// price and pricePendingSetup:true; the DJ picks their real price HERE, the
// first time they're signed in after Stripe verification. Design contract
// (pinned by server tests/price-pending-setup.test.js):
//
//   • Trigger is ACCOUNT STATE, not a route: pricePendingSetup &&
//     isStripeOnboarded, read fresh from GET /users/:slug on every mount of
//     the authenticated shell. Every way back into the app (silent callback
//     login, "log back in to finish", the 14-day resume email, a plain login
//     weeks later) ends in that shell — so the popup can't be missed and the
//     Stripe return-flow files stay 100% untouched.
//   • No dismiss: the only way past is saving a price. Closing the app just
//     re-shows it next open (flag is server-side). Save is one tap — the
//     input is prefilled with the placeholder.
//   • Saving goes through the SAME PATCH /users/:slug/price as the dashboard
//     editor; the server clears the flag atomically with the save.
//   • Provider-agnostic: nothing here knows about Stripe screens — a future
//     payout provider just sets its own "payouts verified" account state.
import React, { useContext, useEffect, useState } from "react";
import { usersApi } from "../services/users";
import { AuthContext } from "../context/AuthContext";
import NumberPad from "./NumberPad";
import PriceFloorNote from "./PriceFloorNote";
import { belowFloor, liftToFloor } from "../utils/priceFloor";
import { currencySymbol, formatAmountForCopy } from "../utils/currency";
import {
  chipsFor,
  defaultPriceFor,
  minRequestPriceFor,
  maxPriceFor,
  needsPriceSetup,
  validatePrice,
} from "../utils/payinConfig";
import { flushNow, track } from "../lib/analytics";
import "./PriceSetupModal.css";
import { t } from "../i18n";

// Human currency names for the subtitle ("In Swedish kronor — change it anytime").
// Shared with the first-run tour's in-tour price step (FirstRunTourPage).
export const CURRENCY_NAMES: Record<string, string> = {
  usd: t("priceSetupModal.usDollars"), eur: "euros", gbp: t("priceSetupModal.britishPounds"), cad: t("priceSetupModal.canadianDollars"),
  chf: t("priceSetupModal.swissFrancs"), sek: t("priceSetupModal.swedishKronor"), dkk: t("priceSetupModal.danishKroner"),
  nok: t("priceSetupModal.norwegianKroner"), pln: t("priceSetupModal.polishZoty"), czk: t("priceSetupModal.czechKoruna"),
  huf: t("priceSetupModal.hungarianForints"), ron: t("priceSetupModal.romanianLei"),
};

// Suffix-symbol currencies render "200 kr", the rest "$20" — same convention
// as utils/currency.ts placeSymbol.
export const SUFFIX_SYMBOL_CURRENCIES = new Set(["sek", "dkk", "nok", "pln", "czk", "huf", "ron", "chf"]);
const SUFFIX = SUFFIX_SYMBOL_CURRENCIES;

export interface ProfileBits {
  slug: string;
  currency?: string;
  pricePerRequest?: number;
  pricePendingSetup?: boolean;
  isStripeOnboarded?: boolean;
  // Fraud door: admin-lifted accounts get the roomier max price.
  capsLifted?: boolean;
  bankCountry?: string;
  // The talent's own minimum request price (server, 2026-10-30).
  minRequestPrice?: number;
}

interface Props {
  // FirstRunTourGate already read the profile to decide on the
  // tour — it hands it over so this popup doesn't read it a second time.
  // Absent (gate read failed, or mounted standalone) → read it here.
  prefetched?: ProfileBits | null;
}

const PriceSetupModal: React.FC<Props> = ({ prefetched }) => {
  const { user } = useContext(AuthContext);
  const [profile, setProfile] = useState<ProfileBits | null>(null);
  const [show, setShow] = useState(false);
  const [price, setPrice] = useState<number>(0);
  const [padOpen, setPadOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    if (!user?.slug) {
      setShow(false);
      return;
    }
    const arm = (data: ProfileBits) => {
      if (needsPriceSetup(data)) {
        setProfile(data);
        setPrice(
          typeof data.pricePerRequest === "number" && data.pricePerRequest > 0
            ? data.pricePerRequest
            : defaultPriceFor(data.currency)
        );
        setShow(true);
      }
    };
    if (prefetched) {
      arm(prefetched);
      return;
    }
    // Fresh read every shell mount: multi-device safe (a price saved on the
    // phone means the laptop's next app-open simply doesn't show the popup).
    usersApi
      .get(user.slug)
      .then((data) => {
        if (alive) arm(data);
      })
      .catch(() => {
        // Can't read the profile → show nothing. The flag is server-side, so
        // the popup simply appears on the next successful load. Never block
        // the app on this fetch.
      });
    return () => {
      alive = false;
    };
  }, [user?.slug, prefetched]);

  if (!show || !profile) return null;

  const currency = String(profile.currency || "usd").toLowerCase();
  const sym = currencySymbol(currency);
  const suffix = SUFFIX.has(currency);
  const chips = chipsFor(currency);
  const check = validatePrice(padOpen && draft !== "" ? Number(draft) : price, currency, profile?.capsLifted, profile);

  const openPad = () => {
    setDraft("");
    setPadOpen(true);
  };

  const closePad = () => {
    const n = parseInt(draft, 10);
    if (draft.trim() !== "" && !isNaN(n)) {
      // Under the floor ("Minimum $2" on the pad) → the floor.
      const v = validatePrice(liftToFloor(n, currency, profile), currency, profile?.capsLifted, profile);
      if (v.ok && typeof v.price === "number" && v.price > 0) setPrice(v.price);
    }
    setDraft("");
    setPadOpen(false);
  };

  const selectChip = (p: number) => {
    setPrice(p);
    setDraft("");
    setPadOpen(false);
  };

  const save = async () => {
    if (saving) return;
    const v = validatePrice(price, currency, profile?.capsLifted, profile);
    if (!v.ok || typeof v.price !== "number" || v.price <= 0) {
      setError(
        t("priceSetupModal.enterAPriceBetween", { amount: formatAmountForCopy(minRequestPriceFor(currency, profile), currency), amount2: formatAmountForCopy(maxPriceFor(currency, profile?.capsLifted), currency) })
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      await usersApi.setPrice(profile.slug, v.price);
      track("onboarding_step_completed", { props: { step: "price_set" } });
      flushNow(); // the reload below would otherwise drop the batch
      // The save cleared pricePendingSetup server-side. Reload so whatever
      // page is underneath (Profile, Requests) shows the real price — this
      // happens exactly once in a DJ's lifetime.
      window.location.reload();
    } catch {
      setError(t("priceSetupModal.couldnTSaveCheck"));
      setSaving(false);
    }
  };

  const shownValue = padOpen ? draft || "0" : String(price);

  return (
    <div className="psm-overlay" role="dialog" aria-modal="true" aria-label={t("priceSetupModal.setYourPrice")}>
      <div className="psm-card">
        <h2 className="psm-title">{t("priceSetupModal.setYourPrice")}</h2>
        <p className="psm-subtitle">
          {t("priceSetupModal.inChangeItAnytime", { value: CURRENCY_NAMES[currency] || currency.toUpperCase() })}
        </p>

        <button type="button" className="psm-price-box" onClick={openPad} aria-label={t("priceSetupModal.editPricePerRequest")}>
          <span className="psm-amount-row">
            {!suffix && <span className="psm-sym">{sym}</span>}
            <span className="psm-value">{shownValue}</span>
            {suffix && <span className="psm-sym psm-sym-suffix">{sym}</span>}
          </span>
          <span className="psm-unit">{t("priceSetupModal.perRequest")}</span>
        </button>

        <div className="psm-chips">
          {chips.map((p) => (
            <button
              key={p}
              type="button"
              className={`psm-chip ${!padOpen && price === p ? "active" : ""}`}
              onClick={() => selectChip(p)}
            >
              {suffix ? `${p.toLocaleString()} ${sym}` : `${sym}${p.toLocaleString()}`}
            </button>
          ))}
        </div>

        {error && <p className="psm-error">{error}</p>}

        <button
          type="button"
          className="psm-save"
          onClick={save}
          disabled={saving || !check.ok}
        >
          {saving ? t("priceSetupModal.saving") : t("priceSetupModal.savePrice")}
        </button>
      </div>

      <NumberPad
        open={padOpen}
        value={draft}
        onChange={setDraft}
        onDone={closePad}
        maxLength={6}
        note={<PriceFloorNote currency={currency} talent={profile} below={belowFloor(draft, currency, profile)} />}
      />
    </div>
  );
};

export default PriceSetupModal;
